/**
 * Top morceaux, classés sur les écoutes ou sur les apprentissages.
 *
 * Catégories nominales — une seule teinte, pas de légende. Barres horizontales
 * parce que les titres sont longs : à la verticale ils s'inclineraient et
 * deviendraient illisibles.
 *
 * Les libellés sont mis en forme (`Artiste — Titre`) plutôt que bruts. Le nom
 * de fichier d'origine reste accessible au survol, pour qui veut le retrouver
 * en base.
 */

import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatCompact, formatInteger } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS } from '../lib/theme';
import type { CatalogTrackItem, DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';

const columns: ReadonlyArray<DataColumn<CatalogTrackItem>> = [
  { key: 'label', header: 'Morceau', render: (row) => row.label },
  { key: 'category', header: 'Catégorie', render: (row) => row.categoryLabel },
  { key: 'difficulty', header: 'Niv.', numeric: true, render: (row) => (row.difficulty ?? '—').toString() },
  { key: 'plays', header: 'Écoutes', numeric: true, render: (row) => formatInteger(row.plays) },
  { key: 'learns', header: 'Apprentissages', numeric: true, render: (row) => formatInteger(row.learns) },
  { key: 'users', header: 'Utilisateurs', numeric: true, render: (row) => formatInteger(row.uniqueUsers) },
];

interface Props {
  data: readonly CatalogTrackItem[];
  metric: 'plays' | 'learns';
}

/** Tronque un libellé trop long pour l'axe. Le tableau donne le nom complet. */
function shorten(label: string, max = 34): string {
  return label.length <= max ? label : `${label.slice(0, max - 1)}…`;
}

export function TopTracksChart({ data, metric }: Props) {
  const isPlays = metric === 'plays';
  const rows = data.map((track) => ({ ...track, short: shorten(track.label) }));
  const height = Math.max(240, rows.length * 30 + 40);

  return (
    <ChartCard
      title={isPlays ? 'Morceaux les plus écoutés' : 'Morceaux les plus appris'}
      subtitle={isPlays ? 'Actions « play » sur la période' : 'Actions « learn » sur la période'}
      rows={rows}
      columns={columns}
      chartHeight={height}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 60, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={formatCompact}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="short"
            tick={{ fill: COLORS.textSecondary, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={225}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<ChartTooltip valueFormatter={formatInteger} />}
          />
          <Bar
            isAnimationActive={false}
            dataKey={metric}
            name={isPlays ? 'Écoutes' : 'Apprentissages'}
            fill={COLORS.series1}
            radius={[0, 4, 4, 0]}
            maxBarSize={BAR_MAX_SIZE}
          >
            <LabelList
              dataKey={metric}
              position="right"
              fill={COLORS.textSecondary}
              fontSize={11}
              formatter={(value: unknown) => formatInteger(Number(value))}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
